import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase/config';
import { collection, getDocs, addDoc, deleteDoc, doc, query, where, updateDoc, arrayUnion } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { Bell, Send, Bold, Italic, Underline, Smile, X, Inbox, Mail, Trash2, Reply, ThumbsUp, AlertCircle } from 'lucide-react';

const NanoMessagingWidget = () => {
  // This widget supports two authentication methods:
  // 1. Email-based auth for staff (HOD, Coordinators, Mentors) - data from 'users' collection
  // 2. Anonymous auth for students (login with roll number + DOB) - data from 'students' collection
  //    Student roll number is stored in localStorage as 'studentRollNumber'
  
  const [currentUser, setCurrentUser] = useState(null);
  const [tab, setTab] = useState('received');
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(true);
  const [authError, setAuthError] = useState(null); // NEW: Track authentication errors
  const [compose, setCompose] = useState(false);
  const [sendType, setSendType] = useState('one'); // 'one', 'all', 'role'
  const [to, setTo] = useState('');
  const [role, setRole] = useState('');
  const [msg, setMsg] = useState('');
  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);
  const [underline, setUnderline] = useState(false);
  const [emoji, setEmoji] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  const emojis = ['😊', '👍', '❤️', '😄', '🎉', '✅', '📚', '💡'];
  const roles = ['HOD', 'Mentoring Co-ordinator', 'Class Co-ordinator', 'Mentor'];
  const templateMessages = ['Okay', 'Thanks', 'Got it', 'Please clarify'];

  // Automatically detect logged-in user from Firebase Auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔐 Auth state changed:', user ? 'User detected' : 'No user');
      
      if (user) {
        try {
          // Check if user is anonymous (student login)
          if (user.isAnonymous) {
            console.log('👤 Anonymous user detected (Student login)');
            
            // For anonymous students, get roll number from localStorage
            const studentRollNumber = localStorage.getItem('studentRollNumber');
            console.log('📝 Roll number from localStorage:', studentRollNumber);
            
            if (studentRollNumber) {
              // Find student in students collection
              const studentsRef = collection(db, 'students');
              const q = query(studentsRef, where('rollNumber', '==', studentRollNumber));
              const snapshot = await getDocs(q);
              
              if (!snapshot.empty) {
                const studentData = snapshot.docs[0].data();
                console.log('✅ Student found in Firestore:', studentData.rollNumber);
                
                setCurrentUser({
                  email: studentData.email || studentData.rollNumber, // Use email if available, otherwise rollNumber
                  name: `${studentData.firstName} ${studentData.surname}`,
                  role: 'Student',
                  rollNumber: studentData.rollNumber
                });
                setAuthError(null); // Clear any previous errors
              } else {
                console.error('❌ Student not found in Firestore');
                setAuthError({
                  type: 'student_not_found',
                  message: 'Student data not found in database',
                  detail: `Roll Number: ${studentRollNumber}`
                });
              }
            } else {
              console.error('❌ Student roll number not found in localStorage');
              setAuthError({
                type: 'no_roll_number',
                message: 'No student login detected',
                detail: 'Please log in through the Student Login page first'
              });
            }
          } else {
            console.log('👔 Email-based user detected (Staff login)');
            console.log('📧 Email:', user.email);
            
            // Regular email-based authentication for staff
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('email', '==', user.email));
            const snapshot = await getDocs(q);
            
            if (!snapshot.empty) {
              const userData = snapshot.docs[0].data();
              console.log('✅ Staff found in Firestore:', userData.role);
              
              setCurrentUser({
                email: userData.email,
                name: userData.name,
                role: userData.role
              });
              setAuthError(null); // Clear any previous errors
            } else {
              console.error('❌ Staff email not found in Firestore users collection');
              setAuthError({
                type: 'user_not_found',
                message: 'Staff account not found in database',
                detail: `Email: ${user.email}`
              });
            }
          }
        } catch (err) {
          console.error('❌ Error fetching user data:', err);
          setAuthError({
            type: 'fetch_error',
            message: 'Failed to load user data',
            detail: err.message
          });
        }
      } else {
        console.log('❌ No Firebase Auth user detected');
        setAuthError({
          type: 'no_auth',
          message: 'Not logged in',
          detail: 'Please log in to access messaging'
        });
      }
      setUserLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load everything
  useEffect(() => {
    if (!currentUser) return; // Wait for user to be loaded

    const load = async () => {
      try {
        const [msgSnap, userSnap, stuSnap] = await Promise.all([
          getDocs(collection(db, 'messages')),
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'students'))
        ]);

        const now = Date.now();
        const msgs = msgSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(m => {
            const time = m.timestamp?.toDate?.().getTime() || 0;
            return now - time < 72 * 60 * 60 * 1000; // 72 hours
          })
          .filter(m => 
            (m.from?.email === currentUser.email || m.to?.email === currentUser.email) &&
            !(m.deletedFor || []).includes(currentUser.email) // Exclude if deleted by current user
          );

        setMessages(msgs.sort((a, b) => 
          (b.timestamp?.toDate?.() || 0) - (a.timestamp?.toDate?.() || 0)
        ));

        setUsers(userSnap.docs.map(d => d.data()));
        setStudents(stuSnap.docs.map(d => ({
          email: d.data().email || d.data().rollNumber, // Use email if available, otherwise use rollNumber
          name: `${d.data().firstName} ${d.data().surname}`,
          roll: d.data().rollNumber
        })));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [currentUser]);

  const send = async () => {
    if (!msg.trim() || msg.length > 40) return;

    const base = {
      from: currentUser,
      message: msg,
      bold, italic, underline,
      timestamp: new Date(),
      read: false
    };

    try {
      if (sendType === 'all') {
        // Send to all students
        await Promise.all(
          students.map(s => 
            addDoc(collection(db, 'messages'), {
              ...base,
              to: { email: s.email, name: s.name, role: 'Student' }
            })
          )
        );
        alert(`Sent to ${students.length} students!`);
      } else if (sendType === 'role') {
        // Send to role
        const targets = users.filter(u => u.role === role);
        await Promise.all(
          targets.map(u => 
            addDoc(collection(db, 'messages'), {
              ...base,
              to: { email: u.email, name: u.name, role: u.role }
            })
          )
        );
        alert(`Sent to ${targets.length} ${role}(s)!`);
      } else {
        // Send to one person
        const person = [...users, ...students.map(s => ({ ...s, role: 'Student' }))]
          .find(p => p.email === to);
        await addDoc(collection(db, 'messages'), {
          ...base,
          to: { email: person.email, name: person.name, role: person.role }
        });
      }

      setMsg('');
      setBold(false);
      setItalic(false);
      setUnderline(false);
      setCompose(false);
      
      // Refresh
      const snap = await getDocs(collection(db, 'messages'));
      const msgs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(m => {
          const now = Date.now();
          const time = m.timestamp?.toDate?.().getTime() || 0;
          return now - time < 72 * 60 * 60 * 1000;
        })
        .filter(m => 
          (m.from?.email === currentUser.email || m.to?.email === currentUser.email) &&
          !(m.deletedFor || []).includes(currentUser.email)
        );

      setMessages(msgs.sort((a, b) => 
        (b.timestamp?.toDate?.() || 0) - (a.timestamp?.toDate?.() || 0)
      ));
    } catch (err) {
      console.error(err);
      alert('Failed to send message');
    }
  };

  const mark = async (id) => {
    try {
      await updateDoc(doc(db, 'messages', id), { read: true });
      setMessages(messages.map(m => (m.id === id ? { ...m, read: true } : m)));
    } catch (err) {
      console.error(err);
    }
  };

  const del = async (id) => {
    try {
      const message = messages.find(m => m.id === id);
      
      // Add current user's email to deletedFor array
      const deletedFor = message.deletedFor || [];
      deletedFor.push(currentUser.email);
      
      await updateDoc(doc(db, 'messages', id), { deletedFor });
      
      // Remove from local state
      setMessages(messages.filter(m => m.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const handleReply = async (messageId, type, templateText = null) => {
    try {
      let replyContent = '';
      
      if (type === 'template') {
        replyContent = templateText;
      } else if (type === 'thumbs') {
        replyContent = '👍';
      } else if (type === 'text') {
        replyContent = replyText.trim();
      }

      if (!replyContent) return;

      const replyData = {
        from: currentUser,
        text: replyContent,
        timestamp: new Date()
      };

      await updateDoc(doc(db, 'messages', messageId), {
        replies: arrayUnion(replyData)
      });

      // Update local state
      setMessages(messages.map(m => 
        m.id === messageId 
          ? { ...m, replies: [...(m.replies || []), replyData] }
          : m
      ));

      // Reset reply state
      setReplyTo(null);
      setReplyText('');
    } catch (err) {
      console.error('Error sending reply:', err);
      alert('Failed to send reply');
    }
  };

  // NEW: Show error state if authentication failed
  if (authError) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 max-w-md mx-auto">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Authentication Error
          </h3>
          <p className="text-red-600 mb-2">{authError.message}</p>
          <p className="text-sm text-gray-600 mb-4">{authError.detail}</p>
          
          {/* Specific instructions based on error type */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
            {authError.type === 'no_auth' && (
              <>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>What to do:</strong>
                </p>
                <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                  <li>Students: Log in through Student Login page</li>
                  <li>Staff: Log in through Staff Login page</li>
                </ul>
              </>
            )}
            
            {authError.type === 'no_roll_number' && (
              <>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>What to do:</strong>
                </p>
                <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                  <li>Go back to Student Login page</li>
                  <li>Enter your Roll Number and Date of Birth</li>
                  <li>Then come back to access messaging</li>
                </ul>
              </>
            )}
            
            {authError.type === 'student_not_found' && (
              <>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>What to do:</strong>
                </p>
                <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                  <li>Contact your Class Coordinator</li>
                  <li>Your account may not be created yet</li>
                  <li>Make sure your Roll Number is correct</li>
                </ul>
              </>
            )}
            
            {authError.type === 'user_not_found' && (
              <>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>What to do:</strong>
                </p>
                <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                  <li>Contact the System Administrator</li>
                  <li>Your staff account may not be set up yet</li>
                  <li>Make sure you're using the correct email</li>
                </ul>
              </>
            )}
            
            {authError.type === 'fetch_error' && (
              <>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>What to do:</strong>
                </p>
                <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                  <li>Refresh the page and try again</li>
                  <li>Check your internet connection</li>
                  <li>Contact support if problem persists</li>
                </ul>
              </>
            )}
          </div>
          
          {/* Debug info (only in development) */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-4 bg-gray-100 rounded-lg p-3 text-left">
              <p className="text-xs font-mono text-gray-700">
                <strong>Debug Info:</strong>
              </p>
              <p className="text-xs font-mono text-gray-600">
                Error Type: {authError.type}
              </p>
              <p className="text-xs font-mono text-gray-600">
                Check browser console for detailed logs
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show loading state
  if (userLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your account...</p>
        </div>
      </div>
    );
  }

  // If no currentUser but also no error (shouldn't happen, but just in case)
  if (!currentUser) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8">
          <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Please log in to access messaging</p>
        </div>
      </div>
    );
  }

  const received = messages.filter(m => m.to?.email === currentUser.email);
  const sent = messages.filter(m => m.from?.email === currentUser.email);
  const unread = received.filter(m => !m.read).length;
  const filtered = tab === 'received' ? received : sent;

  return (
    <div className="bg-white rounded-lg shadow-md p-4 max-w-md mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 pb-2 border-b">
        <div>
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Nano Messaging
          </h3>
          <p className="text-xs text-gray-500">
            {currentUser.name} • {currentUser.role}
          </p>
        </div>
        {currentUser?.role !== 'Student' && (
          <button
            onClick={() => setCompose(!compose)}
            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
          >
            {compose ? <X className="w-4 h-4" /> : <Send className="w-4 h-4" />}
          </button>
        )}
      </div>

      {/* Compose */}
      {compose && currentUser?.role !== 'Student' && (
        <div className="mb-4 p-3 border rounded bg-gray-50">
          <div className="flex gap-2 mb-2">
            {['one', 'all', 'role'].map(t => (
              <button
                key={t}
                onClick={() => setSendType(t)}
                className={`px-2 py-1 text-xs rounded ${
                  sendType === t ? 'bg-blue-600 text-white' : 'bg-gray-200'
                }`}
              >
                {t === 'one' ? 'One' : t === 'all' ? 'All Students' : 'Role'}
              </button>
            ))}
          </div>

          {sendType === 'one' && (
            <select
              value={to}
              onChange={e => setTo(e.target.value)}
              className="w-full p-2 border rounded text-sm mb-2"
            >
              <option value="">Select recipient</option>
              <optgroup label="Staff">
                {users.map(u => (
                  <option key={u.email} value={u.email}>
                    {u.name} ({u.role})
                  </option>
                ))}
              </optgroup>
              <optgroup label="Students">
                {students.map(s => (
                  <option key={s.email} value={s.email}>
                    {s.name} ({s.roll})
                  </option>
                ))}
              </optgroup>
            </select>
          )}

          {sendType === 'role' && (
            <select
              value={role}
              onChange={e => setRole(e.target.value)}
              className="w-full p-2 border rounded text-sm mb-2"
            >
              <option value="">Select role</option>
              {roles.map(r => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          )}

          <input
            type="text"
            value={msg}
            onChange={e => e.target.value.length <= 40 && setMsg(e.target.value)}
            placeholder="Message (max 40 chars)"
            className="w-full p-2 border rounded text-sm mb-2"
            maxLength={40}
          />

          <div className="flex gap-2 mb-2">
            {[
              { key: 'bold', Icon: Bold, state: bold, set: setBold },
              { key: 'italic', Icon: Italic, state: italic, set: setItalic },
              { key: 'underline', Icon: Underline, state: underline, set: setUnderline }
            ].map(({ key, Icon, state, set }) => (
              <button
                key={key}
                onClick={() => set(!state)}
                className={`p-1 rounded ${
                  state ? 'bg-blue-600 text-white' : 'bg-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" />
              </button>
            ))}
            <button
              onClick={() => setEmoji(!emoji)}
              className="p-1 rounded bg-gray-200"
            >
              <Smile className="w-4 h-4" />
            </button>
          </div>

          {emoji && (
            <div className="flex gap-1 mb-2">
              {emojis.map(e => (
                <button
                  key={e}
                  onClick={() => {
                    setMsg(msg + e);
                    setEmoji(false);
                  }}
                  className="text-lg hover:bg-gray-200 rounded px-1"
                >
                  {e}
                </button>
              ))}
            </div>
          )}

          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500">{msg.length}/40</span>
            <button
              onClick={send}
              disabled={
                !msg.trim() ||
                msg.length > 40 ||
                (sendType === 'one' && !to) ||
                (sendType === 'role' && !role)
              }
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-gray-400"
            >
              Send
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-4 mb-3 border-b">
        <button
          onClick={() => {
            setTab('received');
            received.filter(m => !m.read).forEach(m => mark(m.id));
          }}
          className={`flex items-center gap-1 pb-2 text-sm ${
            tab === 'received' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'
          }`}
        >
          <Mail className="w-4 h-4" />
          Received
          {unread > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white rounded-full text-xs">
              {unread}
            </span>
          )}
        </button>
        {currentUser?.role !== 'Student' && (
          <button
            onClick={() => setTab('sent')}
            className={`flex items-center gap-1 pb-2 text-sm ${
              tab === 'sent' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'
            }`}
          >
            <Send className="w-4 h-4" />
            Sent
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">📭</div>
            <p className="text-sm">No messages</p>
          </div>
        ) : (
          filtered.map(m => {
            const other = tab === 'received' ? m.from : m.to;
            const time = m.timestamp?.toDate?.();
            const expires = time ? new Date(time.getTime() + 72 * 60 * 60 * 1000) : null;
            const left = expires ? Math.max(0, Math.floor((expires - new Date()) / 1000 / 60 / 60)) : 0;

            return (
              <div
                key={m.id}
                className={`p-2 rounded border ${
                  !m.read && tab === 'received' ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'
                }`}
              >
                <div className="flex justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-1 mb-1">
                      <span className="font-medium text-sm">{other?.name}</span>
                      <span className="text-xs bg-gray-200 px-1 rounded">{other?.role}</span>
                      {!m.read && tab === 'received' && (
                        <span className="text-xs bg-blue-500 text-white px-1 rounded">New</span>
                      )}
                    </div>
                    <p
                      className={`text-sm mb-1 ${m.bold ? 'font-bold' : ''} ${
                        m.italic ? 'italic' : ''
                      } ${m.underline ? 'underline' : ''}`}
                    >
                      {m.message}
                    </p>

                    {/* Display Replies */}
                    {m.replies && m.replies.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {m.replies.map((reply, idx) => (
                          <div key={idx} className="pl-3 border-l-2 border-blue-300 py-1">
                            <div className="flex items-center gap-1">
                              <span className="text-xs font-medium text-blue-600">{reply.from?.name}</span>
                              <span className="text-xs text-gray-400">replied:</span>
                            </div>
                            <p className="text-sm text-gray-700">
                              {reply.text}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Reply Input (only for received messages) */}
                    {tab === 'received' && replyTo === m.id ? (
                      <div className="mt-2 p-2 bg-white border rounded">
                        {/* Students: Template messages + thumbs up only */}
                        {currentUser?.role === 'Student' ? (
                          <>
                            <div className="text-xs text-gray-600 mb-2">Choose a reply:</div>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              {templateMessages.map((template) => (
                                <button
                                  key={template}
                                  onClick={() => handleReply(m.id, 'template', template)}
                                  className="px-2 py-1.5 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 text-center"
                                >
                                  {template}
                                </button>
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleReply(m.id, 'thumbs')}
                                className="flex-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200 flex items-center justify-center gap-1"
                              >
                                <ThumbsUp className="w-3 h-3" />
                                Thumbs Up
                              </button>
                              <button
                                onClick={() => {
                                  setReplyTo(null);
                                  setReplyText('');
                                }}
                                className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"
                              >
                                Cancel
                              </button>
                            </div>
                          </>
                        ) : (
                          /* Non-students: Text input + thumbs up */
                          <>
                            <div className="flex gap-2 mb-2">
                              <input
                                type="text"
                                value={replyText}
                                onChange={(e) => e.target.value.length <= 25 && setReplyText(e.target.value)}
                                placeholder="Reply (max 25 chars)..."
                                className="flex-1 px-2 py-1 text-xs border rounded"
                                maxLength={25}
                              />
                              <button
                                onClick={() => handleReply(m.id, 'text')}
                                disabled={!replyText.trim()}
                                className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:bg-gray-400"
                              >
                                Send
                              </button>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleReply(m.id, 'thumbs')}
                                className="flex-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200 flex items-center justify-center gap-1"
                              >
                                <ThumbsUp className="w-3 h-3" />
                                Thumbs Up
                              </button>
                              <button
                                onClick={() => {
                                  setReplyTo(null);
                                  setReplyText('');
                                }}
                                className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"
                              >
                                Cancel
                              </button>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">{replyText.length}/25</div>
                          </>
                        )}
                      </div>
                    ) : tab === 'received' && (
                      <button
                        onClick={() => setReplyTo(m.id)}
                        className="mt-1 text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <Reply className="w-3 h-3" />
                        Reply
                      </button>
                    )}

                    <div className="text-xs text-gray-500 mt-1">
                      {time?.toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                      {' • '}
                      {left}h left
                    </div>
                  </div>
                  {/* Delete button for both received and sent */}
                  <button 
                    onClick={() => del(m.id)} 
                    className="ml-2 p-1 text-red-600 hover:bg-red-50 rounded"
                    title="Delete for me"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="mt-3 pt-2 border-t text-xs text-gray-500 text-center">
        Auto-delete after 72h • Max 40 chars • 
        {currentUser?.role === 'Student' 
          ? ' Students: Reply with templates or 👍' 
          : ' Reply max 25 chars'}
      </div>
    </div>
  );
};

export default NanoMessagingWidget;
